import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { FuelLabel, getFuelColor } from "../../../utils/fuelLabelColor";
import { formatCompactLitres } from "../../../utils/fuelStockDisplay";

const parsePercent = (value) =>
  Math.max(0, Number(String(value || 0).replace(/,/g, "")) || 0);

const resolveFillMetrics = (fuel) => {
  const stockLitres = Number(fuel.current_stock || 0);
  const capacityLitres = Number(fuel.capacity_litres || 0);
  const fromRatio =
    capacityLitres > 0 ? (stockLitres / capacityLitres) * 100 : null;
  const fillPct = fromRatio !== null ? fromRatio : parsePercent(fuel.percent);
  const chartPct = Math.min(100, Math.max(0, fillPct));
  const isOverfill = Boolean(fuel.is_overfill) || fillPct > 100.01;

  return { stockLitres, capacityLitres, fillPct, chartPct, isOverfill };
};

const stockStatus = (fillPct, isOverfill) => {
  if (isOverfill) {
    return { label: "Overfill", className: "fuel-stock-card__status--overfill" };
  }
  if (fillPct >= 50) return { label: "Healthy", className: "fuel-stock-card__status--healthy" };
  if (fillPct >= 25) return { label: "Moderate", className: "fuel-stock-card__status--moderate" };
  if (fillPct > 0) return { label: "Low", className: "fuel-stock-card__status--low" };
  return { label: "Empty", className: "fuel-stock-card__status--empty" };
};

const formatPct = (value) => {
  const n = Number(value || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/**
 * Donut chart card for one fuel type — true fill %, litres, and tank drill-down.
 */
const FuelStockChart = ({ fuel, onSelect }) => {
  const { stockLitres, capacityLitres, fillPct, chartPct, isOverfill } =
    resolveFillMetrics(fuel);
  const fuelColor = getFuelColor(fuel.fuel_name);
  const emptyPct = Math.max(0, 100 - chartPct);
  const status = stockStatus(fillPct, isOverfill);
  const pctLabel = formatPct(fillPct);

  const chartData = [
    { key: "filled", value: chartPct > 0 ? chartPct : 0 },
    { key: "empty", value: emptyPct > 0 ? emptyPct : chartPct > 0 ? 0 : 100 },
  ];

  return (
    <button
      type="button"
      className={`fuel-stock-card${isOverfill ? " fuel-stock-card--overfill" : ""}`}
      style={{ "--fuel-accent": fuelColor }}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(fuel);
        event.currentTarget.blur();
      }}
      aria-label={`${fuel.fuel_name} ${pctLabel} percent full`}
    >
      <span className={`fuel-stock-card__status ${status.className}`}>{status.label}</span>

      <div className="fuel-stock-card__chart-wrap">
        <ResponsiveContainer width="100%" height={128}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="82%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={chartPct > 0 && emptyPct > 0 ? 2 : 0}
              stroke="none"
              isAnimationActive
              animationDuration={700}
            >
              <Cell fill={isOverfill ? "#dc2626" : fuelColor} />
              <Cell fill="#e8edf3" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div className="fuel-stock-card__center" aria-hidden="true">
          <span className="fuel-stock-card__pct">{pctLabel}%</span>
          <span className="fuel-stock-card__pct-label">filled</span>
        </div>
      </div>

      <div className="fuel-stock-card__meta">
        <FuelLabel name={fuel.fuel_name} className="fuel-stock-card__name" />
        <span className="fuel-stock-card__litres">{formatCompactLitres(stockLitres)}</span>
        {Number(fuel.wac_purchase_price) > 0 ? (
          <span className="fuel-stock-card__capacity">
            WAC purchase: ₹{Number(fuel.wac_purchase_price).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}/L
          </span>
        ) : null}
        {capacityLitres > 0 ? (
          <span className="fuel-stock-card__capacity">
            of {formatCompactLitres(capacityLitres)} capacity
          </span>
        ) : null}
        {isOverfill ? (
          <span className="fuel-stock-card__warn">Stock exceeds tank capacity — verify dip / book stock</span>
        ) : null}
      </div>
    </button>
  );
};

export default FuelStockChart;
